import moment from "moment";

/**
 * Quarter Management Utilities
 * Centralized utilities for handling quarter logic across the application
 */

// Quarter to month mapping
export const QUARTER_MONTHS = {
  1: { start: 7, end: 9 }, // July - September
  2: { start: 10, end: 1 }, // October - January
  3: { start: 2, end: 3 }, // February - March
  4: { start: 4, end: 6 }, // April - June
};

// Database field mappings for quarters
export const QUARTER_DB_FIELDS = {
  grades: {
    1: "firstQuarter",
    2: "secondQuarter",
    3: "thirdQuarter",
    4: "fourthQuarter",
  },
  conducts: {
    1: "firstQuarter",
    2: "secondQuarter",
    3: "thirdQuarter",
    4: "fourthQuarter",
  },
  conductDetails: {
    1: "firstQuarter1",
    2: "secondQuarter1",
    3: "thirdQuarter1",
    4: "fourthQuarter1",
  },
};

/**
 * Get the current quarter based on the current month
 * @returns {number} Current quarter (1-4)
 */
export const getCurrentQuarter = () => {
  const currentMonth = moment().month() + 1; // moment().month() returns 0-11, we need 1-12
  
  if (currentMonth >= 7 && currentMonth <= 9) {
    return 1;
  } else if ((currentMonth >= 10 && currentMonth <= 12) || currentMonth === 1) {
    return 2;
  } else if (currentMonth >= 2 && currentMonth <= 3) {
    return 3;
  } else if (currentMonth >= 4 && currentMonth <= 6) {
    return 4;
  }
  
  // Default to first quarter if outside school year
  return 1;
};

/**
 * Get the quarter label for display
 * @param {number} quarterNumber - Quarter number (1-4)
 * @returns {string} Quarter label
 */
export const getQuarterLabel = (quarterNumber) => {
  const labels = {
    1: "1st Quarter",
    2: "2nd Quarter",
    3: "3rd Quarter",
    4: "4th Quarter",
  };
  return labels[quarterNumber] || "Unknown Quarter";
};

/**
 * Get the date range for a specific quarter
 * @param {number} quarter - Quarter number (1-4)
 * @param {number} schoolYear - School year (e.g., 2024)
 * @returns {Object} Object with start and end dates
 */
export const getQuarterDateRange = (quarter, schoolYear) => {
  const quarterMonths = QUARTER_MONTHS[quarter];
  if (!quarterMonths) {
    throw new Error(`Invalid quarter: ${quarter}`);
  }

  let startYear = schoolYear;
  let endYear = schoolYear;
  
  // For quarters that span across years
  if (quarter === 2) {
    endYear = schoolYear + 1;
  }

  const start = moment()
    .year(startYear)
    .month(quarterMonths.start - 1)
    .startOf("month");

  const end = moment()
    .year(endYear)
    .month(quarterMonths.end - 1)
    .endOf("month");

  return {
    start: start.toDate(),
    end: end.toDate(),
    startFormatted: start.format("MMMM YYYY"),
    endFormatted: end.format("MMMM YYYY"),
  };
};

/**
 * Get the database field name for a specific quarter and data type
 * @param {number} quarter - Quarter number (1-4)
 * @param {string} dataType - Data type ('grades', 'conducts', 'conductDetails')
 * @param {number} detailIndex - Detail index for conduct details (1-5)
 * @returns {string} Database field name
 */
export const getQuarterDbField = (quarter, dataType = "grades", detailIndex = null) => {
  const fields = QUARTER_DB_FIELDS[dataType];
  if (!fields) {
    throw new Error(`Invalid data type: ${dataType}`);
  }

  const field = fields[quarter];
  if (!field) {
    throw new Error(`Invalid quarter: ${quarter}`);
  }

  // For conduct details, append the detail index
  if (dataType === "conductDetails" && detailIndex) {
    return `${field}${detailIndex}`;
  }

  return field;
};

/**
 * Check if a quarter is valid
 * @param {number} quarter - Quarter number to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidQuarter = (quarter) => {
  return quarter >= 1 && quarter <= 4;
};

/**
 * Get all quarters as an array of objects for dropdowns
 * @returns {Array} Array of quarter objects
 */
export const getQuarterOptions = () => {
  return [
    { key: "1", text: "1st Quarter", value: 1 },
    { key: "2", text: "2nd Quarter", value: 2 },
    { key: "3", text: "3rd Quarter", value: 3 },
    { key: "4", text: "4th Quarter", value: 4 },
  ];
};

/**
 * Get the next quarter
 * @param {number} currentQuarter - Current quarter (1-4)
 * @returns {number} Next quarter (1-4)
 */
export const getNextQuarter = (currentQuarter) => {
  if (!isValidQuarter(currentQuarter)) {
    throw new Error(`Invalid quarter: ${currentQuarter}`);
  }
  return currentQuarter === 4 ? 1 : currentQuarter + 1;
};

/**
 * Get the previous quarter
 * @param {number} currentQuarter - Current quarter (1-4)
 * @returns {number} Previous quarter (1-4)
 */
export const getPreviousQuarter = (currentQuarter) => {
  if (!isValidQuarter(currentQuarter)) {
    throw new Error(`Invalid quarter: ${currentQuarter}`);
  }
  return currentQuarter === 1 ? 4 : currentQuarter - 1;
};

/**
 * Check if a quarter is complete based on current date
 * @param {number} quarter - Quarter to check (1-4)
 * @param {number} schoolYear - School year
 * @returns {boolean} True if quarter is complete
 */
export const isQuarterComplete = (quarter, schoolYear) => {
  const quarterRange = getQuarterDateRange(quarter, schoolYear);
  return moment().isAfter(quarterRange.end);
};

/**
 * Get current school year based on current date
 * @returns {number} Current school year
 */
export const getCurrentSchoolYear = () => {
  const currentDate = moment();
  const currentMonth = currentDate.month() + 1;
  const currentYear = currentDate.year();
  
  // School year starts in July
  return currentMonth >= 7 ? currentYear : currentYear - 1;
};